import { useEffect, useState } from 'react';

const GLYPHS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789#$%&*+=?@';
/** Short: the secret should be there almost at once — the effect is a flourish, not a wait. */
const DURATION_MS = 210;

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** One frame of the effect: characters left of the cursor are final, the rest still scramble. */
export function scrambleFrame(text: string, progress: number, random: () => number = Math.random): string {
  const settled = Math.floor(text.length * Math.min(1, Math.max(0, progress)));
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i] ?? '';
    if (i < settled || char === '\n' || char === ' ') out += char;
    else out += GLYPHS[Math.floor(random() * GLYPHS.length)] ?? char;
  }
  return out;
}

/**
 * Renders text as if it were being decrypted in front of the viewer: random
 * glyphs settle into the real characters from left to right. Viewers who
 * prefer reduced motion get the text at once.
 */
export function DecryptText({ text, className }: { readonly text: string; readonly className?: string }): React.JSX.Element {
  const [shown, setShown] = useState(() => (reducedMotion() ? text : scrambleFrame(text, 0)));
  const [done, setDone] = useState(() => reducedMotion());

  useEffect(() => {
    if (reducedMotion()) return undefined;
    const started = performance.now();
    let frame = 0;
    const tick = (now: number): void => {
      const progress = (now - started) / DURATION_MS;
      if (progress >= 1) {
        setShown(text);
        setDone(true);
        return;
      }
      setShown(scrambleFrame(text, progress));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [text]);

  return (
    <span className={className} data-decrypting={done ? 'false' : 'true'} aria-live="off">
      {shown}
    </span>
  );
}
